import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Emoji or lucide icon rendered as floating decoration
const THEMES = {
  study: {
    gradient: "from-[#B4F8C8] via-[#FDFBF7] to-[#FBE7C6]",
    icons: ["📚", "🧠", "✏️", "🧪", "📐", "🔬", "🧮", "📖"],
    ink: "#0A0A0A",
  },
  career: {
    gradient: "from-[#FFAEBC] via-[#FDFBF7] to-[#FBE7C6]",
    icons: ["🧭", "💼", "🚀", "🎯", "🪜", "📈", "🗺️", "🏆"],
    ink: "#0A0A0A",
  },
  ideas: {
    gradient: "from-[#FBE7C6] via-[#FDFBF7] to-[#FFAEBC]",
    icons: ["💡", "🛠️", "⚡", "🔧", "🎨", "🚀", "💰", "🔥"],
    ink: "#0A0A0A",
  },
  skills: {
    gradient: "from-[#A0E7E5] via-[#FDFBF7] to-[#B4F8C8]",
    icons: ["🗣️", "⏱️", "💻", "🤝", "🎤", "🎧", "📱", "⚙️"],
    ink: "#0A0A0A",
  },
  meghalaya: {
    gradient: "from-[#A0E7E5] via-[#B4F8C8] to-[#FBE7C6]",
    icons: ["🏔️", "🌲", "🌿", "🌸", "🦋", "🌺", "🍃", "☘️"],
    ink: "#0A0A0A",
  },
  profile: {
    gradient: "from-[#FBE7C6] via-[#FDFBF7] to-[#A0E7E5]",
    icons: ["🎖️", "🏅", "⭐", "🎯", "🚀", "🏆", "✨", "💫"],
    ink: "#0A0A0A",
  },
  auth: {
    gradient: "from-[#FFAEBC] via-[#FDFBF7] to-[#A0E7E5]",
    icons: ["🔑", "✉️", "🚀", "✨", "🎉", "👋", "💫", "🌟"],
    ink: "#0A0A0A",
  },
  home: {
    gradient: "from-[#B4F8C8] via-[#FDFBF7] to-[#FFAEBC]",
    icons: ["🚀", "📚", "💡", "🧭", "⚡", "🎯", "✨", "🏆"],
    ink: "#0A0A0A",
  },
};

function routeToKey(path) {
  if (path.startsWith("/study")) return "study";
  if (path.startsWith("/career")) return "career";
  if (path.startsWith("/ideas")) return "ideas";
  if (path.startsWith("/skills")) return "skills";
  if (path.startsWith("/meghalaya")) return "meghalaya";
  if (path.startsWith("/community")) return "ideas";
  if (path.startsWith("/u/")) return "profile";
  if (path.startsWith("/profile")) return "profile";
  if (path.startsWith("/auth")) return "auth";
  return "home";
}

// Deterministic pseudo-random from string
function seed(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return () => {
    h = (h * 9301 + 49297) & 0x7fffffff;
    return (h % 10000) / 10000;
  };
}

export default function ModuleBackdrop() {
  const loc = useLocation();
  const key = routeToKey(loc.pathname);
  const theme = THEMES[key] || THEMES.home;
  const r = seed(key);

  const floaters = Array.from({ length: 14 }).map((_, i) => ({
    id: i,
    emoji: theme.icons[i % theme.icons.length],
    left: r() * 100,
    top: r() * 90,
    size: 22 + Math.floor(r() * 36),
    delay: r() * 6,
    dur: 8 + r() * 8,
    rot: Math.floor(r() * 40) - 20,
    xDrift: (r() - 0.5) * 40,
    yDrift: (r() - 0.5) * 40,
    opacity: 0.14 + r() * 0.18,
  }));

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={key}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 pointer-events-none overflow-hidden"
        style={{ zIndex: 0 }}
        data-testid={`module-backdrop-${key}`}
      >
        {/* Coloured gradient wash unique to this module */}
        <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`} />
        {/* Grid dots overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(rgba(0,0,0,0.08) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
            opacity: 0.6,
          }}
        />
        {/* Floating themed icons */}
        {floaters.map((f) => (
          <motion.div
            key={f.id}
            className="absolute select-none"
            style={{ left: `${f.left}%`, top: `${f.top}%`, fontSize: f.size, opacity: f.opacity }}
            animate={{ x: [0, f.xDrift, 0], y: [0, f.yDrift, 0], rotate: [f.rot, -f.rot, f.rot] }}
            transition={{ duration: f.dur, repeat: Infinity, ease: "easeInOut", delay: f.delay }}
          >
            {f.emoji}
          </motion.div>
        ))}
        {/* Big neo-brutalist blobs */}
        <motion.div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full nb-border"
          style={{ background: "rgba(255,255,255,0.35)", boxShadow: "10px 10px 0 0 #0A0A0A" }}
          animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-24 -right-16 w-[420px] h-[420px] rounded-[40%] nb-border"
          style={{ background: "rgba(255,255,255,0.25)", boxShadow: "-10px -10px 0 0 #0A0A0A" }}
          animate={{ y: [0, -30, 0], rotate: [0, 8, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </motion.div>
    </AnimatePresence>
  );
}
